import {
  ConceptsApiError,
  InstitutionalConceptApiError,
  createConcept,
  getConcepts,
  setConceptActive,
  updateConcept,
  type ConceptCategoryContract,
  type ConceptContract,
  type ConceptKind,
} from '@urbanik/api-client';
import {
  Button,
  CanonicalActionMenu,
  CanonicalFilterBar,
  CanonicalFilterField,
  CanonicalModal,
  CanonicalNotice,
  CanonicalPageHeader,
  DropdownItem,
  DataTable,
  EmptyState,
  FilterActions,
  FormActions,
  PageSizeSelector,
  Pagination,
  ResultsCounter,
  SearchField,
  SelectField,
  StatePanel,
  StatusBadge,
  TextAreaField,
  TextField,
  canonicalStates,
  type DataColumn,
  type SortDirection,
} from '@urbanik/ui';
import { Ellipsis, Layers3, ListFilter, Pencil, Plus, Power, Tags } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { apiBase } from './api-base';
import './concepts.css';

type Draft = {
  name: string;
  description: string;
  categoryId: string;
  kind: ConceptKind;
};
type StatusFilter = 'all' | 'active' | 'inactive';
const empty: Draft = { name: '', description: '', categoryId: '', kind: 'standard' };
const normalize = (value: string) =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLocaleLowerCase('es')
    .trim();

export type ConceptsTransport = {
  list: (signal: AbortSignal) => Promise<{
    items: readonly ConceptContract[];
    categories: readonly ConceptCategoryContract[];
  }>;
  create: (draft: Draft) => Promise<unknown>;
  update: (id: string, draft: Draft & { expectedVersion: number }) => Promise<unknown>;
  status: (concept: ConceptContract) => Promise<unknown>;
};
const defaultTransport: ConceptsTransport = {
  list: (signal) => getConcepts(apiBase, signal),
  create: (draft) => createConcept(apiBase, draft),
  update: (id, draft) => updateConcept(apiBase, id, draft),
  status: (concept) => setConceptActive(apiBase, concept.id, !concept.active),
};
export function ConceptsView({
  canCreate,
  canUpdate,
  canActivate,
  transport = defaultTransport,
}: {
  canCreate: boolean;
  canUpdate: boolean;
  canActivate: boolean;
  transport?: ConceptsTransport;
}) {
  const [items, setItems] = useState<readonly ConceptContract[]>([]),
    [categories, setCategories] = useState<readonly ConceptCategoryContract[]>([]),
    [state, setState] = useState<'loading' | 'error' | 'empty' | 'success'>('loading'),
    [editing, setEditing] = useState<ConceptContract | null>(null),
    [open, setOpen] = useState(false),
    [draft, setDraft] = useState<Draft>(empty),
    [saving, setSaving] = useState(false),
    [notice, setNotice] = useState(''),
    [search, setSearch] = useState(''),
    [categoryFilter, setCategoryFilter] = useState(''),
    [kindFilter, setKindFilter] = useState<'' | ConceptKind>(''),
    [statusFilter, setStatusFilter] = useState<StatusFilter>('all'),
    [sort, setSort] = useState<{ columnId: string; direction: SortDirection }>({
      columnId: 'code',
      direction: 'ascending',
    }),
    [page, setPage] = useState(1),
    [pageSize, setPageSize] = useState(10);

  const mounted = useRef(false);
  const activeRequest = useRef<AbortController | null>(null);
  const load = useCallback(() => {
    activeRequest.current?.abort();
    setState('loading');
    const controller = new AbortController();
    activeRequest.current = controller;
    transport
      .list(controller.signal)
      .then((result) => {
        if (controller.signal.aborted) return;
        setItems(result.items);
        setCategories(result.categories);
        setState(result.items.length ? 'success' : 'empty');
      })
      .catch(() => {
        if (!controller.signal.aborted) {
          setItems([]);
          setCategories([]);
          setState('error');
        }
      });
    return () => controller.abort();
  }, [transport]);
  useEffect(() => {
    mounted.current = true;
    load();
    return () => {
      mounted.current = false;
      activeRequest.current?.abort();
    };
  }, [load]);

  const start = (concept?: ConceptContract) => {
    setEditing(concept ?? null);
    setDraft(
      concept
        ? {
            name: concept.name,
            description: concept.description,
            categoryId: concept.category.id,
            kind: concept.kind,
          }
        : { ...empty, categoryId: categories.find((category) => category.active)?.id ?? '' },
    );
    setOpen(true);
  };
  const save = async () => {
    setSaving(true);
    try {
      if (editing)
        await transport.update(editing.id, { ...draft, expectedVersion: editing.version });
      else await transport.create(draft);
      if (!mounted.current) return;
      setOpen(false);
      setNotice(editing ? 'Concepto actualizado.' : 'Concepto creado.');
      load();
    } catch (error) {
      if (!mounted.current) return;
      setNotice(
        error instanceof ConceptsApiError || error instanceof InstitutionalConceptApiError
          ? error.message
          : 'No pudimos guardar el concepto.',
      );
    } finally {
      if (mounted.current) setSaving(false);
    }
  };
  const toggle = async (concept: ConceptContract) => {
    try {
      await transport.status(concept);
      if (!mounted.current) return;
      setNotice(concept.active ? 'Concepto desactivado.' : 'Concepto activado.');
      load();
    } catch (error) {
      if (!mounted.current) return;
      setNotice(error instanceof Error ? error.message : 'No pudimos cambiar el estado.');
    }
  };
  const resetPage = (action: () => void) => {
    action();
    setPage(1);
  };
  const clearFilters = () => {
    setSearch('');
    setCategoryFilter('');
    setKindFilter('');
    setStatusFilter('all');
    setPage(1);
  };
  const hasFilters = Boolean(
    search.trim() || categoryFilter || kindFilter || statusFilter !== 'all',
  );

  const filtered = useMemo(() => {
    const term = normalize(search);
    const valueFor = (concept: ConceptContract, columnId: string) => {
      if (columnId === 'code') return concept.code;
      if (columnId === 'name') return concept.name;
      if (columnId === 'category') return concept.category.name;
      if (columnId === 'kind') return concept.kind;
      if (columnId === 'status') return concept.active ? 'Activo' : 'Inactivo';
      return '';
    };
    return items
      .filter((concept) => {
        const matchesSearch =
          !term ||
          [concept.code, concept.name, concept.description, concept.category.name].some((value) =>
            normalize(value).includes(term),
          );
        return (
          matchesSearch &&
          (!categoryFilter || concept.category.id === categoryFilter) &&
          (!kindFilter || concept.kind === kindFilter) &&
          (statusFilter === 'all' || concept.active === (statusFilter === 'active'))
        );
      })
      .sort((left, right) => {
        const comparison = valueFor(left, sort.columnId).localeCompare(
          valueFor(right, sort.columnId),
          'es',
          { numeric: true, sensitivity: 'base' },
        );
        return sort.direction === 'ascending' ? comparison : -comparison;
      });
  }, [categoryFilter, items, kindFilter, search, sort, statusFilter]);
  const pages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const currentPage = Math.min(page, pages);
  const paged = filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const columns: readonly DataColumn<ConceptContract>[] = [
    { id: 'code', header: 'Código', accessor: (concept) => concept.code, sortable: true },
    {
      id: 'name',
      header: 'Concepto',
      accessor: (concept) => concept.name,
      sortable: true,
      cell: (concept) => (
        <span className="concepts-name-cell">
          <strong>{concept.name}</strong>
          <small>{concept.description || 'Sin descripción'}</small>
        </span>
      ),
    },
    {
      id: 'category',
      header: 'Categoría',
      accessor: (concept) => concept.category.name,
      sortable: true,
    },
    {
      id: 'kind',
      header: 'Clasificación',
      accessor: (concept) =>
        concept.kind === 'regularization_contribution' ? 'Regularización como aporte' : 'Estándar',
      sortable: true,
    },
    {
      id: 'status',
      header: 'Estado',
      accessor: (concept) => (concept.active ? 'Activo' : 'Inactivo'),
      sortable: true,
      cell: (concept) => (
        <StatusBadge tone={concept.active ? 'success' : 'neutral'}>
          {concept.active ? 'Activo' : 'Inactivo'}
        </StatusBadge>
      ),
    },
    {
      id: 'actions',
      header: 'Acciones',
      accessor: () => '',
      cell: (concept) => (
        <div className="ui-row-actions">
          <CanonicalActionMenu
            icon={<Ellipsis aria-hidden="true" />}
            label={`Más acciones para ${concept.name}`}
          >
            {canUpdate ? (
              <DropdownItem icon={<Pencil aria-hidden="true" />} onSelect={() => start(concept)}>
                Editar
              </DropdownItem>
            ) : null}
            {canActivate ? (
              <DropdownItem
                icon={<Power aria-hidden="true" />}
                onSelect={() => void toggle(concept)}
              >
                {concept.active ? 'Desactivar' : 'Activar'}
              </DropdownItem>
            ) : null}
          </CanonicalActionMenu>
        </div>
      ),
    },
  ];

  return (
    <section className="concepts-page">
      <CanonicalPageHeader
        title="Conceptos"
        description="Catálogo institucional y categorías, incluida la regularización como aporte."
        actions={
          canCreate ? (
            <Button variant="primary" icon={<Plus aria-hidden="true" />} onClick={() => start()}>
              Nuevo concepto
            </Button>
          ) : null
        }
      />
      {notice ? (
        <CanonicalNotice tone="info" message={notice} onDismiss={() => setNotice('')} />
      ) : null}
      {state !== 'success' ? (
        <StatePanel
          state={canonicalStates.find(
            (item) => item.kind === (state === 'empty' ? 'empty' : state),
          )!}
          onAction={() => load()}
        />
      ) : (
        <>
          <CanonicalFilterBar>
            <SearchField value={search} onChange={(value) => resetPage(() => setSearch(value))} />
            <CanonicalFilterField icon={<Tags />}>
              <SelectField
                label="Categoría"
                value={categoryFilter}
                onValueChange={(value) => resetPage(() => setCategoryFilter(value))}
                options={[
                  { value: '', label: 'Todas las categorías' },
                  ...categories.map((category) => ({ value: category.id, label: category.name })),
                ]}
              />
            </CanonicalFilterField>
            <CanonicalFilterField icon={<Layers3 />}>
              <SelectField
                label="Clasificación"
                value={kindFilter}
                onValueChange={(value) => resetPage(() => setKindFilter(value as '' | ConceptKind))}
                options={[
                  { value: '', label: 'Todas' },
                  { value: 'standard', label: 'Estándar' },
                  { value: 'regularization_contribution', label: 'Regularización como aporte' },
                ]}
              />
            </CanonicalFilterField>
            <CanonicalFilterField icon={<ListFilter />}>
              <SelectField
                label="Estado"
                value={statusFilter}
                onValueChange={(value) => resetPage(() => setStatusFilter(value as StatusFilter))}
                options={[
                  { value: 'all', label: 'Todos los estados' },
                  { value: 'active', label: 'Activos' },
                  { value: 'inactive', label: 'Inactivos' },
                ]}
              />
            </CanonicalFilterField>
            {hasFilters ? (
              <FilterActions>
                <Button icon={<ListFilter aria-hidden="true" />} onClick={clearFilters}>
                  Limpiar filtros
                </Button>
              </FilterActions>
            ) : null}
          </CanonicalFilterBar>
          <section className="concepts-table-shell" aria-label="Listado de conceptos">
            <DataTable
              paginate={false}
              rows={paged}
              columns={columns}
              visibleColumnIds={new Set(columns.map((column) => column.id))}
              sort={sort}
              onSort={(columnId) => {
                setSort((current) => ({
                  columnId,
                  direction:
                    current.columnId === columnId && current.direction === 'ascending'
                      ? 'descending'
                      : 'ascending',
                }));
                setPage(1);
              }}
              empty={
                <EmptyState
                  title="Sin resultados"
                  description="No encontramos conceptos que coincidan con la búsqueda y los filtros."
                  action={
                    <Button icon={<ListFilter aria-hidden="true" />} onClick={clearFilters}>
                      Limpiar filtros
                    </Button>
                  }
                />
              }
            />
            <footer className="ui-data-table-footer">
              <ResultsCounter
                from={filtered.length ? (currentPage - 1) * pageSize + 1 : 0}
                to={filtered.length ? (currentPage - 1) * pageSize + paged.length : 0}
                total={filtered.length}
                noun="conceptos"
              />
              <Pagination page={currentPage} pages={pages} onChange={setPage} />
              <PageSizeSelector
                value={pageSize}
                options={[5, 10, 20, 50]}
                onChange={(value) => resetPage(() => setPageSize(value))}
                label="Registros por página"
              />
            </footer>
          </section>
        </>
      )}
      <CanonicalModal
        open={open}
        title={editing ? 'Editar concepto' : 'Nuevo concepto'}
        description="Los valores y reglas financieras no forman parte de esta subfase."
        onClose={() => setOpen(false)}
      >
        <div className="concepts-form">
          <TextField
            label="Código institucional"
            value={editing?.code ?? 'Se generará al guardar'}
            disabled
            hint="Identificador técnico único, por ejemplo CON-000001. Servirá para referenciar el concepto de forma estable en reglas, reportes e integraciones."
          />
          <TextField
            label="Nombre"
            hint="Denominación visible que reconocerán los usuarios en formularios, estados de cuenta y reportes."
            required
            value={draft.name}
            onChange={(event) => setDraft({ ...draft, name: event.currentTarget.value })}
          />
          <SelectField
            label="Categoría"
            hint="Agrupa conceptos para organizarlos, filtrarlos y presentarlos de manera coherente."
            required
            value={draft.categoryId}
            options={categories
              .filter((category) => category.active)
              .map((category) => ({ value: category.id, label: category.name }))}
            onValueChange={(categoryId) => setDraft({ ...draft, categoryId })}
          />
          <SelectField
            label="Clasificación"
            hint="Indica si es un concepto estándar o si la regularización debe tratarse institucionalmente como aporte."
            required
            value={draft.kind}
            options={[
              { value: 'standard', label: 'Estándar' },
              { value: 'regularization_contribution', label: 'Regularización como aporte' },
            ]}
            onValueChange={(kind) => setDraft({ ...draft, kind: kind as ConceptKind })}
          />
          <TextAreaField
            label="Descripción"
            hint="Explica el propósito del concepto. No define valores, periodicidad ni condiciones."
            value={draft.description}
            onChange={(event) => setDraft({ ...draft, description: event.currentTarget.value })}
          />
          <FormActions>
            <Button onClick={() => setOpen(false)}>Cancelar</Button>
            <Button variant="primary" disabled={saving} onClick={() => void save()}>
              {saving ? 'Guardando…' : 'Guardar concepto'}
            </Button>
          </FormActions>
        </div>
      </CanonicalModal>
    </section>
  );
}
